import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { AIAssistantGetViewImageOutput } from 'balus-common';

type Props = {
  output: unknown;
};

function toViewImageOutput(output: unknown): AIAssistantGetViewImageOutput | undefined {
  const parsed = v.safeParse(AIAssistantGetViewImageOutput, output);
  return parsed.success ? parsed.output : undefined;
}

/**
 * getViewImage ツールがAIへ渡した画像を、ユーザーにも見せるカード。
 *
 * 画像は履歴へ保存する前に取り除かれる（sanitizeAIAssistantMessagesForHistory）ため、
 * 履歴を復元した会話では画像本体が無く、取得した事実だけが残る。
 *
 * ツール結果を含むメッセージは、後続のテキストがストリーミングされる間ずっと再レンダーされる。
 * data URL を毎回組み立てると数百KBの文字列を作り直すことになるため、base64 の参照が同じ間は
 * useMemo で使い回し、src が同一参照であることで React の props 比較も O(1) に収める。
 */
export const ViewImageCard = memo(function ViewImageCard({ output }: Props) {
  const { t } = useTranslation();
  const viewImage = toViewImageOutput(output);
  // 依存はオブジェクトではなく文字列にする。output が作り直されても base64 の参照は同じなので、
  // これで再レンダー時の再組み立てを防げる。
  const mimeType = viewImage?.image?.mimeType;
  const dataBase64 = viewImage?.image?.dataBase64;
  const dataUrl = useMemo(
    () => (mimeType && dataBase64 ? `data:${mimeType};base64,${dataBase64}` : undefined),
    [mimeType, dataBase64]
  );
  if (!viewImage) return null;

  const caption = viewImage.viewName
    ? `${viewImage.viewName}（${viewImage.width}×${viewImage.height}px）`
    : `${viewImage.width}×${viewImage.height}px`;

  return (
    <div className="my-1 flex flex-col gap-1.5 rounded border border-gray-300 bg-white p-2 text-xs">
      {dataUrl ? (
        <img
          src={dataUrl}
          alt={t('viewModel.aiChat.viewImageCardAlt', { viewName: viewImage.viewName })}
          className="max-h-64 w-full rounded border border-gray-200 object-contain"
        />
      ) : (
        <span className="text-gray-400">{t('viewModel.aiChat.viewImageCardExpiredMessage')}</span>
      )}
      <span className="truncate text-gray-500" title={caption}>
        {caption}
      </span>
    </div>
  );
});
